import { useForm } from "@inertiajs/react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import type { RequestItem } from "@/types";

interface ProductOption {
  id: number;
  value: number;
  label: string;
  internal_code?: string;
}

interface NewInvoiceItemModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId: number;
  availableRequestItems: RequestItem[];
  hasPurchaseRequest: boolean;
  unitOptions: { value: string; label: string }[];
}

const parseCommaNumber = (raw: string): number | null => {
  if (raw.trim() === "") return null;
  if (!/^[0-9]+(,[0-9]+)?$/.test(raw)) return null;
  const parsed = Number(raw.replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
};

export default function NewInvoiceItemModal({
  open,
  onOpenChange,
  invoiceId,
  availableRequestItems,
  hasPurchaseRequest,
  unitOptions,
}: NewInvoiceItemModalProps) {
  const routes = useOrganizationRoutes();
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [qtyStr, setQtyStr] = useState("");
  const [priceStr, setPriceStr] = useState("");
  const [vatStr, setVatStr] = useState("");

  // For async product search (when no PR)
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductOption | null>(
    null,
  );

  const { data, setData, post, processing, reset } = useForm({
    invoice_item: {
      invoice_id: invoiceId,
      description_extracted: "",
      quantity: 1,
      unit: "UN",
      unit_price: 0,
      vat_rate: null as number | null,
    },
  });

  // For tracking selected request item when PR exists
  const [selectedRequestItemId, setSelectedRequestItemId] = useState<
    number | null
  >(null);

  // Debounced product search
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchProducts = useCallback(
    async (term: string) => {
      if (term.length < 2) {
        setProductOptions([]);
        return;
      }
      setIsSearching(true);
      try {
        const response = await fetch(
          `${routes.products.search()}?term=${encodeURIComponent(term)}`,
        );
        if (response.ok) {
          const results = (await response.json()) as ProductOption[];
          setProductOptions(results);
        }
      } catch (error) {
        console.error("Error searching products:", error);
      } finally {
        setIsSearching(false);
      }
    },
    [routes.products],
  );

  const handleProductSearch = useCallback(
    (term: string) => {
      setProductSearchTerm(term);
      setSelectedProduct(null);
      setData("invoice_item", {
        ...data.invoice_item,
        description_extracted: term,
      });
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(() => {
        void searchProducts(term);
      }, 300);
    },
    [data.invoice_item, searchProducts, setData],
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleRequestItemSelection = (requestItemId: number | null) => {
    if (requestItemId) {
      const selectedItem = availableRequestItems.find(
        (item) => item.id === requestItemId,
      );
      if (selectedItem) {
        setSelectedRequestItemId(requestItemId);
        setData("invoice_item", {
          ...data.invoice_item,
          description_extracted: selectedItem.description,
          quantity: selectedItem.quantity,
          unit: selectedItem.unit || "UN",
        });
        setQtyStr(String(selectedItem.quantity).replace(".", ","));
      }
    } else {
      setSelectedRequestItemId(null);
      setData("invoice_item", {
        ...data.invoice_item,
        description_extracted: "",
        quantity: 1,
        unit: "UN",
      });
      setQtyStr("");
    }
  };

  const handleProductSelection = (product: ProductOption) => {
    setSelectedProduct(product);
    setData("invoice_item", {
      ...data.invoice_item,
      description_extracted: product.label,
    });
    setSelectorOpen(false);
    setProductSearchTerm("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!data.invoice_item.description_extracted.trim()) return;

    post(routes.invoices.items(), {
      preserveScroll: true,
      onSuccess: () => {
        handleClose();
      },
    });
  };

  const handleClose = () => {
    reset();
    setQtyStr("");
    setPriceStr("");
    setVatStr("");
    setSelectedRequestItemId(null);
    setSelectedProduct(null);
    setProductSearchTerm("");
    setProductOptions([]);
    onOpenChange(false);
  };

  const isQtyValid = parseCommaNumber(qtyStr) !== null || qtyStr === "";
  const isPriceValid = parseCommaNumber(priceStr) !== null || priceStr === "";
  const isVatValid = vatStr.trim() === "" || parseCommaNumber(vatStr) !== null;

  const canSubmit =
    data.invoice_item.description_extracted.trim() !== "" &&
    data.invoice_item.quantity > 0 &&
    data.invoice_item.unit_price >= 0 &&
    !processing;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Agregar Ítem a la Factura</DialogTitle>
          <DialogDescription>
            {hasPurchaseRequest
              ? "Selecciona un ítem de la solicitud de compra o ingresa los datos manualmente."
              : "Busca un producto o ingresa la descripción manualmente."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Product/Item Selection */}
          <div className="space-y-2">
            <Label>
              {hasPurchaseRequest ? "Ítem de Solicitud" : "Producto"} (opcional)
            </Label>
            {hasPurchaseRequest ? (
              <Popover open={selectorOpen} onOpenChange={setSelectorOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={selectorOpen}
                    className="w-full justify-between font-normal"
                  >
                    {selectedRequestItemId ? (
                      (() => {
                        const item = availableRequestItems.find(
                          (item) => item.id === selectedRequestItemId,
                        );
                        return item
                          ? `Item #${item.item_number} - ${item.description}`
                          : "Ítem no encontrado";
                      })()
                    ) : (
                      <span className="text-muted-foreground">
                        Selecciona un ítem o escribe manualmente...
                      </span>
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-96 p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar ítem..." />
                    <CommandList>
                      <CommandEmpty>No se encontraron ítems.</CommandEmpty>
                      <CommandGroup>
                        {availableRequestItems.map((item) => (
                          <CommandItem
                            key={item.id}
                            value={`${item.item_number} ${item.description}`}
                            onSelect={() => {
                              handleRequestItemSelection(item.id);
                              setSelectorOpen(false);
                            }}
                            className="flex flex-col items-start gap-1 p-3"
                          >
                            <div className="flex w-full items-center gap-2">
                              <Check
                                className={`mr-2 h-4 w-4 ${
                                  selectedRequestItemId === item.id
                                    ? "opacity-100"
                                    : "opacity-0"
                                }`}
                              />
                              <div className="flex-1">
                                <div className="font-medium">
                                  Item #{item.item_number}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {item.description}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  Cantidad: {item.quantity} {item.unit}
                                </div>
                              </div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            ) : (
              <Popover open={selectorOpen} onOpenChange={setSelectorOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={selectorOpen}
                    className="w-full justify-between font-normal"
                  >
                    {selectedProduct ? (
                      <span className="truncate">{selectedProduct.label}</span>
                    ) : (
                      <span className="text-muted-foreground">
                        Buscar producto o escribir manualmente...
                      </span>
                    )}
                    <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-96 p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Buscar producto..."
                      value={productSearchTerm}
                      onValueChange={handleProductSearch}
                    />
                    <CommandList>
                      {isSearching ? (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                          Buscando...
                        </div>
                      ) : productOptions.length === 0 &&
                        productSearchTerm.length >= 2 ? (
                        <CommandEmpty>
                          No se encontraron productos.
                        </CommandEmpty>
                      ) : (
                        <CommandGroup>
                          {productOptions.map((product) => (
                            <CommandItem
                              key={product.id}
                              value={product.label}
                              onSelect={() => handleProductSelection(product)}
                              className="flex items-center gap-2 p-3"
                            >
                              <Check
                                className={`h-4 w-4 ${
                                  selectedProduct?.id === product.id
                                    ? "opacity-100"
                                    : "opacity-0"
                                }`}
                              />
                              <div className="flex-1">
                                <div className="font-medium">
                                  {product.label}
                                </div>
                                {product.internal_code && (
                                  <Badge variant="outline" className="text-xs">
                                    {product.internal_code}
                                  </Badge>
                                )}
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
          </div>

          {/* Quantity and Unit */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="quantity">Cantidad *</Label>
              <Input
                id="quantity"
                type="text"
                inputMode="decimal"
                value={qtyStr}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (/^[0-9]*[,]?[0-9]*$/.test(raw)) {
                    setQtyStr(raw);
                    const parsed = parseCommaNumber(raw);
                    setData("invoice_item", {
                      ...data.invoice_item,
                      quantity: parsed ?? data.invoice_item.quantity,
                    });
                  }
                }}
                placeholder="Ej: 10 o 10,5"
                className={!isQtyValid && qtyStr ? "border-red-500" : ""}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="unit">Unidad *</Label>
              <Select
                value={data.invoice_item.unit}
                onValueChange={(value) =>
                  setData("invoice_item", {
                    ...data.invoice_item,
                    unit: value,
                  })
                }
              >
                <SelectTrigger id="unit">
                  <SelectValue placeholder="Selecciona unidad" />
                </SelectTrigger>
                <SelectContent>
                  {unitOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Unit Price and VAT */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="unit_price">Precio Unitario *</Label>
              <Input
                id="unit_price"
                type="text"
                inputMode="decimal"
                value={priceStr}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (/^[0-9]*[,]?[0-9]*$/.test(raw)) {
                    setPriceStr(raw);
                    const parsed = parseCommaNumber(raw);
                    setData("invoice_item", {
                      ...data.invoice_item,
                      unit_price: parsed ?? data.invoice_item.unit_price,
                    });
                  }
                }}
                placeholder="Ej: 1000 o 1000,50"
                className={!isPriceValid && priceStr ? "border-red-500" : ""}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="vat_rate">IVA % (opcional)</Label>
              <Input
                id="vat_rate"
                type="text"
                inputMode="decimal"
                value={vatStr}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (/^[0-9]*[,]?[0-9]*$/.test(raw)) {
                    setVatStr(raw);
                    const parsed = parseCommaNumber(raw);
                    setData("invoice_item", {
                      ...data.invoice_item,
                      vat_rate: parsed,
                    });
                  }
                }}
                placeholder="Ej: 21"
                className={!isVatValid && vatStr ? "border-red-500" : ""}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {processing ? "Guardando..." : "Agregar Ítem"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
